import { defineStore } from 'pinia';
import { ref } from 'vue';
import { 
    getCartList, 
    updateCartItem, 
    deleteCartItem, 
    checkCartItems,
    type CartShop,
    type CartItem
} from '@/api/cart';

export const useCartStore = defineStore('cart', () => {
    const cartList = ref<CartShop[]>([]);
    const totalCount = ref(0);
    const totalPrice = ref(0);
    const loading = ref(false);

    // Helper: Update TabBar Badge
    const updateTabBarBadge = () => {
        const count = totalCount.value;
        if (count > 0) {
            const text = count > 99 ? '99+' : count.toString();
            uni.setTabBarBadge({
                index: 2,
                text: text
            }).catch(err => {
                console.log('setTabBarBadge fail', err); 
            });
        } else {
            uni.removeTabBarBadge({
                index: 2
            }).catch(() => {});
        }
    };

    // Fetch List
    const fetchList = async (silent = false) => {
        if (!silent) loading.value = true;
        try {
            const res = await getCartList();
            // request 拦截器已解包 res.data，所以这里 res 即为 CartListResponse
            if (res && res.list) {
                // 数据类型转换：后端 Decimal 转 string，前端需转 number 避免 toFixed 报错
                const safeParseFloat = (val: any) => {
                    if (typeof val === 'number') return val;
                    return parseFloat(val) || 0;
                };

                // 处理列表项中的金额
                const formattedList = res.list.map(shop => ({
                    ...shop,
                    items: shop.items.map(item => ({
                        ...item,
                        price: safeParseFloat(item.price),
                        original_price: item.original_price ? safeParseFloat(item.original_price) : undefined,
                        rebate_amount: safeParseFloat(item.rebate_amount)
                    }))
                }));

                cartList.value = formattedList;
                totalCount.value = res.total_count;
                totalPrice.value = safeParseFloat(res.total_price);
            } else {
                // 空购物车情况
                cartList.value = [];
                totalCount.value = 0;
                totalPrice.value = 0;
            }
            // Update Badge
            updateTabBarBadge();
        } catch (error) {
            console.error(error);
        } finally {
            loading.value = false;
        }
    };

    // Update Quantity
    const updateQuantity = async (item: CartItem, quantity: number) => {
        if (quantity <= 0) return;
        try {
            await updateCartItem(item.id, quantity);
            item.quantity = quantity;
            // 重新计算总价（或者重新拉取列表）
            // 简单起见，重新拉取列表以保证金额准确
            await fetchList();
        } catch (error) {
            console.error(error);
        }
    };

    // Delete Item
    const deleteItem = async (sIdx: number, iIdx: number) => {
        const shop = cartList.value[sIdx];
        const item = shop.items[iIdx];
        try {
            await deleteCartItem(item.id);
            uni.showToast({ title: '删除成功', icon: 'success' });
            await fetchList();
        } catch (error) {
            console.error(error);
        }
    };

    // Check Item (Single Merchant Logic)
    const checkItem = async (sIdx: number, iIdx: number, checked: boolean) => {
        const shop = cartList.value[sIdx];
        const item = shop.items[iIdx];

        // 如果是选中操作，检查互斥
        if (checked) {
            const otherSelected = cartList.value.some((s, idx) => idx !== sIdx && s.items.some(i => i.selected));
            if (otherSelected) {
                uni.showToast({ title: '因商户返利不同，订单请分开结算', icon: 'none' });
                // 这里我们不做自动清除，而是阻止勾选
                // 前端状态回滚
                // item.selected = false; // 已经在组件 v-model 中处理了？
                // 如果是调用接口，需要传 clear_others=false
                // 但如果用户确实想选这个，应该先取消其他的。
                // 现在的交互是“阻止并提示”，所以直接返回，不调接口
                return false; 
            }
        }

        try {
            await checkCartItems({ sku_ids: [item.sku_id], selected: checked });
            item.selected = checked;
            // 检查商户全选状态
            shop.selected = shop.items.every(i => i.selected);
            await fetchList(); // 刷新总价
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    // Check Shop
    const checkShop = async (sIdx: number, checked: boolean) => {
        const shop = cartList.value[sIdx];
        
        if (checked) {
            const otherSelected = cartList.value.some((s, idx) => idx !== sIdx && s.items.some(i => i.selected));
            if (otherSelected) {
                uni.showToast({ title: '因商户返利不同，订单请分开结算', icon: 'none' });
                return false;
            }
        }

        const skuIds = shop.items.map(i => i.sku_id);
        try {
            await checkCartItems({ sku_ids: skuIds, selected: checked });
            shop.selected = checked;
            shop.items.forEach(i => i.selected = checked);
            // 使用静默刷新，避免勾选时页面闪烁
            await fetchList(true);
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    return {
        cartList,
        totalCount,
        totalPrice,
        loading,
        fetchList,
        updateQuantity,
        deleteItem,
        checkItem,
        checkShop,
        updateTabBarBadge
    };
});
